import { Injectable } from '@angular/core';
import { ApiService } from './api.service';
import { EMPTY, interval, Subject, throwError, timer } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  finalize,
  map,
  mergeMap,
  switchMap,
  take,
  takeUntil,
  tap
} from 'rxjs/operators';

import { HttpParams } from '@angular/common/http';

interface PathParam {
  param: string;
  replaceWith: string;
}

interface HttpOptions {
  body?: any;
  headers?: any;
  reportProgress?: boolean;
  params?:
    | HttpParams
    | {
        [param: string]: string | string[];
      };
  responseType?: 'json';
  withCredentials?: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class HeartbeatService {
  queue: any[];

  constructor(private apiService: ApiService) {
    this.queue = [];
  }

  addToQueue(
    apiObject: any,
    payload: any,
    callInterval: number,
    maxHits: number,
    removeFromQueueOnError: boolean,
    executeImmediately?: boolean,
    options?: HttpOptions,
    urlPathParams?: PathParam[]
  ) {
    // check if API Object already exists in queue, if yes, do not add
    const queueIndexOfAPI = this.queue.findIndex(item => item.apiObject.endPoint === apiObject.endPoint);
    if (queueIndexOfAPI > -1) {
      return this.queue[queueIndexOfAPI].intObs;
    }

    if (!maxHits || maxHits === -1) {
      // lets put a sane number
      maxHits = 60;
    }

    if (callInterval < 1000) {
      // too fast, throttle
      callInterval = 10000;
    }

    const newQueueItemIndex =
      this.queue.push({
        apiObject,
        payload,
        callInterval,
        maxHits,
        removeFromQueueOnError,
        executeImmediately,
        state: new Subject<void>()
      }) - 1;

    console.info('API URL : ' + apiObject.endPoint + ' - added to Heartbeat queue \u2665');

    return this.startTick(newQueueItemIndex);
  }

  startTick(queueIndex: number) {
    const queueRef = this.queue[queueIndex];
    if (queueRef.executeImmediately) {
      this.queue[queueIndex].intObs = timer(0, queueRef.callInterval);
    } else {
      this.queue[queueIndex].intObs = interval(queueRef.callInterval);
    }

    this.queue[queueIndex].apiObs = this.queue[queueIndex].intObs.pipe(
      tap((ev: any) => {
        console.log(
          'API URL : ' +
            queueRef.apiObject.endPoint +
            ' | queueIndex: ' +
            queueIndex +
            ' called. DateTime: ' +
            new Date() +
            ' Times Called: ' +
            (ev + 1)
        );
      }),
      take(queueRef.maxHits),
      takeUntil(queueRef.state),
      // distinctUntilChanged(),
      switchMap(intTick => {
        // switchMap would cancel previous pending request on new tick
        return this.callAPI(queueRef);
      }),
      finalize(() => {
        console.log(
          'API URL : ' +
            queueRef.apiObject.endPoint +
            ' | queueIndex: ' +
            queueIndex +
            ' called. DateTime: ' +
            new Date() +
            ' Execution Complete \u221A \u2665'
        );
        this.removeFromQueue(queueRef);
      })
    );
    return this.queue[queueIndex].apiObs;
  }

  removeFromQueue(queueRef: any, apiObj?: any) {
    let queueIndexOfAPI;

    if (apiObj) {
      queueIndexOfAPI = this.queue.findIndex(item => item.apiObject.endPoint === apiObj.endPoint);
      if (queueIndexOfAPI > -1) {
        queueRef = this.queue[queueIndexOfAPI];
      }
    }

    if (!queueRef || (queueRef && !queueRef.state)) {
      return;
    }
    queueRef.state.next();

    if (queueRef.state) {
      queueRef.state.complete();
    }

    queueIndexOfAPI = this.queue.findIndex(item => item.apiObject.endPoint === queueRef.apiObject.endPoint);
    if (queueIndexOfAPI > -1) {
      this.queue[queueIndexOfAPI].state = null;
      this.queue.splice(queueIndexOfAPI, 1);
      console.info('API URL : ' + queueRef.apiObject.endPoint + ' - removed from Heartbeat queue \u2665');
    }
    console.log(this.queue);
  }

  callAPI(queueRef: any) {
    let apiObs;
    if (queueRef.apiObject.method === 'GET') {
      queueRef.inProgress = true;
      apiObs = this.apiService.get(queueRef.apiObject).pipe(
        catchError(err => {
          if (queueRef.removeFromQueueOnError) {
            this.removeFromQueue(queueRef);
          }
          queueRef.inProgress = false;
          return EMPTY; // throwError(err);
        })
      );
    } else if (queueRef.apiObject.method === 'POST') {
      queueRef.inProgress = true;
      apiObs = this.apiService
        .post(queueRef.apiObject, queueRef.payload, queueRef.options, queueRef.urlPathParams)
        .pipe(
          map(response => {
            // return the modified data:
            queueRef.inProgress = false;
            return response;
          }),
          catchError(err => {
            if (queueRef.removeFromQueueOnError) {
              this.removeFromQueue(queueRef);
            }
            queueRef.inProgress = false;
            return EMPTY; // throwError(err);
          })
        );
    }
    return apiObs;
  }

  updatePayload(apiObject: any, payload: any) {
    const queueIndexOfAPI = this.queue.findIndex(item => item.apiObject.endPoint === apiObject.endPoint);
    if (queueIndexOfAPI > -1) {
      this.queue[queueIndexOfAPI].payload = JSON.parse(JSON.stringify(payload));
      console.info('API URL : ' + apiObject.endPoint + ' - got its payload updated! \u2665');
    }
  }
}
